import React, { useState, useEffect } from 'react';
import { all_routes } from '../../../router/all_routes';
import { Link, useSearchParams } from 'react-router-dom';
import Table from "../../../../core/common/dataTable/index";
import CollapseHeader from '../../../../core/common/collapse-header/collapse-header';
import { API } from '../../../../common/Constants';
import useFetch from '../../../../hooks/useFetch';
import { getAPIHeaders } from '../../../../common/Utils'; 
import PreLoader from '../../../../core/common/preLoader';
import AddSubscription from './subscriptionAdd';
import ModifySubscription from './subscriptionModify';
import CopyRightInfo from '../../../../core/common/copyRightInfo';
import { useAccess } from '../../../../hooks/accessContext';
import DeleteModalV2 from '../../../../core/modals/deleteModalV2';
import CustomToast from '../../../../core/common/customToast';
import "../../../../style/css/shared.scss";



const Subscriptions = () => {
  const { hasAcademicMgntCreate, hasAcademicMgntDelete, hasAcademicMgntEdit } = useAccess();
  const { isLoading, error, callFetch } = useFetch();
  const [searchParams] = useSearchParams();
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [editId, setEditId] = useState<number>(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [toastSettings, setToastSettings] = useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO";
  }>({
    show: false,
    message: "Something went wrong please try again later",
    heading: "Oops! Something went wrong",
    type: "ERROR",
  });
  const [viewType, setViewType] = useState<'table' | 'card'>('table');

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  useEffect(() => {
    if (error) {
      setToastSettings((prev) => ({
        ...prev,
        show: true,
        message: error?.response?.data || "Something went wrong, please try again later.",
      }));
    }
  }, [error]);

  useEffect(() => {
    if (searchParams.get('courseId') && searchParams.get('action')) {
      if(searchParams.get('action') === 'add'){
        setShowAddModal(true);
      }
    }
  }, [searchParams]);

  const fetchSubscriptions = () => {
    callFetch(
      `${API.SUBSCRIPTION_PLAN}/details?includeCourses=true`,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((response) => {
      setSubscriptions(response || []);
    }).catch((err) => {
      console.error('Error fetching subscriptions:', err);
      setToastSettings((prev) => ({
        ...prev,
        show: true,
        message: "Failed to fetch subscriptions. Please try again.",
        heading: "Error",
        type: "ERROR",
      }));
    });
  };

  const deleteData = (id: number) => {
    // For now, using static data
    setSubscriptions(prev => prev.filter(sub => sub.subscriptionPlanId !== id));
    setToastSettings((prev) => ({
      ...prev,
      show: true,
      message: "Subscription deleted successfully",
      heading: "Success",
      type: "SUCCESS",
    }));
    
    // callFetch(
    //   `${API.SUBSCRIPTION_PLAN}/${id}`,
    //   'DELETE',
    //   getAPIHeaders(),
    //   null,
    //   null
    // )
    //   .then(() => {
    //     fetchSubscriptions();
    //   })
    //   .catch((error) => {
    //     setToastSettings((prev) => ({
    //       ...prev,
    //       show: true,
    //       message: error?.response?.data || "Something went wrong, please try again later.",
    //     }));
    //   });
  };

  const toggleActive = (id: number) => {
    // Find the current subscription to determine its current status
    const currentSubscription = subscriptions.find(sub => sub.subscriptionPlanId === id);
    if (!currentSubscription) return;

    const isCurrentlyActive = currentSubscription.planIsActive;
    const endpoint = isCurrentlyActive ? 'deactivate' : 'activate';
    
    // Optimistically update the UI
    setSubscriptions(prev => 
      prev.map(sub => 
        sub.subscriptionPlanId === id ? { ...sub, planIsActive: !sub.planIsActive } : sub
      )
    );

    // Make API call
    callFetch(
      `${API.SUBSCRIPTION_PLAN}/${id}/${endpoint}`,
      'PUT',
      getAPIHeaders(),
      null,
      null
    ).then(() => {
      setToastSettings((prev) => ({
        ...prev,
        show: true,
        message: `Subscription ${isCurrentlyActive ? 'deactivated' : 'activated'} successfully`,
        heading: "Success",
        type: "SUCCESS",
      }));
    }).catch((err) => {
      // Revert the optimistic update on error
      setSubscriptions(prev => 
        prev.map(sub => 
          sub.subscriptionPlanId === id ? { ...sub, planIsActive: isCurrentlyActive } : sub
        )
      );
      
      console.error('Error toggling subscription status:', err);
      setToastSettings((prev) => ({
        ...prev,
        show: true,
        message: `Failed to ${isCurrentlyActive ? 'deactivate' : 'activate'} subscription. Please try again.`,
        heading: "Error",
        type: "ERROR",
      }));
    });
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "subscriptionPlanId",
      render: (text: String, record: any) => (
        <h6 className="fw-medium text-center">
          {text}
        </h6>
      ),
      sorter: (a: any, b: any) => a.subscriptionPlanId - b.subscriptionPlanId,
      width: "8%",
      align: "center" as "center",
    },
    {
      title: "Subscription",
      dataIndex: "subscriptionPlanName",
      render: (text: String, record: any) => (
        <div className="d-flex align-items-center">
          <div className="avatar avatar-sm me-2 flex-shrink-0 bg-light-primary rounded-circle">
            <span className="text-primary fw-bold">
              {text?.charAt(0)?.toUpperCase()}
            </span>
          </div>
          <div className="subscription-info">
            <h6 className="fw-medium mb-0 text-truncate" title={String(text)}>
              {text}
            </h6>
            {record.subscriptionDescription && (
              <p
                className="text-muted mb-0 small subscription-description"
                title={record.subscriptionDescription}
              >
                {record.subscriptionDescription}
              </p>
            )}
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => a.subscriptionPlanName.localeCompare(b.subscriptionPlanName),
      width: "27%",
    },
    {
      title: "Plan Type",
      dataIndex: "planType",
      render: (text: string, record: any) => {
        const getBadgeClass = (type: string) => {
          switch (type) {
            case 'ENTERPRISE':
              return 'bg-danger';
            case 'PREMIUM':
              return 'bg-success';
            case 'BASIC':
              return 'bg-info';
            default:
              return 'bg-secondary';
          }
        };
        
        return (
          <span className={`badge ${getBadgeClass(text)}`}>
            {text}
          </span>
        );
      },
      sorter: (a: any, b: any) => a.planType.localeCompare(b.planType),
      width: "10%",
      align: "center" as "center",
    },
    {
      title: "Duration",
      dataIndex: "duration",
      render: (text: String, record: any) => (
        text ?
          (<div className="text-dark fw-medium" >
            {text} <span className="fs-12 text-secondary" > months</span>
          </div>)
          :
          <div className="text-dark fw-medium" >Lifetime</div>
      ),
      sorter: (a: any, b: any) => a.duration - b.duration,
      width: "10%",
      align: "center" as "center",
    },
    {
      title: "Price",
      dataIndex: "pricingInfo",
      render: (pricingInfo: any, record: any) => (
        <div className="text-center">
          <h6 className="fw-medium mb-0">
            {pricingInfo?.currencyCode === 'INR' ? '₹' : pricingInfo?.currencyCode} { pricingInfo?.promotionalPrice >= 0
             ? pricingInfo?.promotionalPrice?.toLocaleString() : pricingInfo?.price?.toLocaleString()}
          </h6>
          {pricingInfo?.promotionalPrice >= 0 && pricingInfo?.promotionalPrice !== pricingInfo?.price && (
            <small className="text-muted text-decoration-line-through">
              {pricingInfo?.currencyCode === 'INR' ? '₹' : pricingInfo?.currencyCode} {pricingInfo?.price?.toLocaleString() || 0}
            </small>
          )}
        </div>
      ),
      sorter: (a: any, b: any) => (a.pricingInfo?.price || 0) - (b.pricingInfo?.price || 0),
      width: "10%",
      align: "center" as "center",
    },
    {
      title: "Enrollments",
      dataIndex: "totalEnrollments",
      render: (text: number, record: any) => (
        <div className="text-center">
          <div className="mb-1">
            <span className="badge bg-primary me-1">Total: {record.totalEnrollments}</span>
          </div>
          <div>
            <span className="badge bg-success me-1">Active: {record.activeEnrollments}</span>
            <span className="badge bg-warning">Pending: {record.pendingEnrollments}</span>
          </div>
        </div>
      ),
      width: "15%",
      align: "center" as "center",
    },
    {
      title: "Status",
      dataIndex: "planIsActive",
      render: (text: boolean, record: any) => (
        <div className="form-check form-switch d-flex justify-content-center">
          <input
            className="form-check-input"
            type="checkbox"
            checked={text}
            onChange={() => toggleActive(record.subscriptionPlanId)}
            disabled={!hasAcademicMgntEdit}
          />
        </div>
      ),
      width: "10%",
      align: "center" as "center",
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (text: String, record: any) => (
        <div className="d-flex justify-content-center">
          {hasAcademicMgntEdit && (
            <button
              className="btn btn-sm btn-outline-primary me-2"
              onClick={() => {
                setEditId(record.subscriptionPlanId)
                setShowEditModal(true)
              }}
            >
              <i className="ti ti-edit" />
            </button>
          )}
          {hasAcademicMgntDelete && (
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => {
                setItemToDelete(record);
                setShowDeleteModal(true);
              }}
            >
              <i className="ti ti-trash" />
            </button>
          )}
        </div>
      ),
      width: "15%",
      align: "center" as "center",
    },
  ];

  return (
    <>
      <PreLoader showLoader={isLoading} />
      <DeleteModalV2
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        heading="Confirm Delete"
        message={itemToDelete ? `Are you sure you want to delete subscription "${itemToDelete.subscriptionPlanName}"?` : ""}
        onDelete={() => {
          if (itemToDelete) {
            deleteData(itemToDelete.subscriptionPlanId);
            setShowDeleteModal(false);
            setItemToDelete(null);
          }
        }}
      />
      <CustomToast
        show={toastSettings?.show}
        message={toastSettings?.message}
        heading={toastSettings?.heading}
        type={toastSettings?.type}
        onClose={() => {
          setToastSettings((prev) => ({
            ...prev,
            show: false,
          }));
        }}
      />
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Subscriptions</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Online Learning Center</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Subscriptions
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="d-flex mb-2 right-content gap-3">
                <button
                  disabled={!hasAcademicMgntCreate}
                  onClick={() => {
                    setEditId(-1)
                    setShowAddModal(true)
                  }}
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-circle-plus me-2" />
                  Add Subscription Plan
                </button>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Subscription List</h5>
              <div className="view-toggle">
                <button 
                  className={`btn btn-sm me-2 ${viewType === 'table' ? 'btn-primary' : 'btn-outline-primary'}`} 
                  onClick={() => setViewType('table')}
                  aria-label="Switch to table view"
                >
                  <i className="ti ti-table me-1"></i> Table View
                </button>
                <button 
                  className={`btn btn-sm ${viewType === 'card' ? 'btn-primary' : 'btn-outline-primary'}`} 
                  onClick={() => setViewType('card')}
                  aria-label="Switch to card view"
                >
                  <i className="ti ti-layout-grid me-1"></i> Card View
                </button>
              </div>
            </div>
            <div className="card-body p-0">
              {viewType === 'table' ? (
                <Table
                  dataSource={subscriptions}
                  columns={columns}
                  Selection={false}
                  refresh={fetchSubscriptions}
                />
              ) : (
                <div className="subscription-cards p-3">
                  <div className="row">
                    {subscriptions.map((subscription: any) => (
                      <div className="col-xl-4 col-lg-6 col-md-6 mb-3" key={subscription.subscriptionPlanId}>
                        <div className="card h-100 border shadow-hover transition-all">
                          <div className="card-body position-relative">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <div className="d-flex align-items-center">
                                <div className="avatar avatar-sm me-3 flex-shrink-0 bg-light-primary rounded-circle">
                                  <span className="text-primary fw-bold">
                                    {subscription.subscriptionPlanName?.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <h6 className="mb-0 fw-semibold subscription-title">
                                  <Link 
                                    to="#" 
                                    className="text-reset text-decoration-none"
                                    onClick={() => { setEditId(subscription?.subscriptionPlanId) }}
                                  >
                                    {subscription.subscriptionPlanName}
                                  </Link>
                                </h6>
                              </div>
                              <span className={`badge ${subscription.planType === 'ENTERPRISE' ? 'badge-soft-danger' : subscription.planType === 'PREMIUM' ? 'badge-soft-success' : subscription.planType === 'BASIC' ? 'badge-soft-info' : 'badge-soft-secondary'}`}>
                                {subscription.planType}
                              </span>
                            </div>
                            
                            {subscription.subscriptionDescription && (
                              <p
                                className="fs-12 text-muted mb-3 subscription-description"
                                title={subscription.subscriptionDescription}
                              >
                                {subscription.subscriptionDescription}
                              </p>
                            )}
                            
                            <div className="mb-3">
                              <div className="d-flex justify-content-between mb-2">
                                <div className="fs-12 fw-medium text-dark">Duration</div>
                                <span className="badge badge-soft-info">{subscription.durationType}</span>
                              </div>

                              <div className="d-flex justify-content-between mb-2">
                                <div className="fs-12 fw-medium text-dark">Price</div>
                                <span className="fw-semibold text-primary">
                                  {subscription.pricingInfo?.currencyCode || '₹'}{subscription.pricingInfo?.price?.toLocaleString() || 0}
                                </span>
                              </div>

                              <div className="d-flex justify-content-between mb-2">
                                <div className="fs-12 fw-medium text-dark">Enrollments</div>
                                <div className="text-end">
                                  <div className="fs-10 text-muted">Total: {subscription.totalEnrollments}</div>
                                  <div className="fs-10 text-success">Active: {subscription.activeEnrollments}</div>
                                </div>
                              </div>

                              <div className="d-flex justify-content-between">
                                <div className="fs-12 fw-medium text-dark">Status</div>
                                <div>
                                  {subscription.planIsActive ? (
                                    <span className="badge badge-soft-success d-flex align-items-center">
                                      <i className="ti ti-circle-filled fs-8 me-1"></i> Active
                                    </span>
                                  ) : (
                                    <span className="badge badge-soft-danger d-flex align-items-center">
                                      <i className="ti ti-circle-filled fs-8 me-1"></i> Inactive
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div className="d-flex justify-content-end mt-3">
                              <Link
                                hidden={!hasAcademicMgntEdit}
                                to="#"
                                className="btn btn-sm btn-outline-primary me-2"
                                onClick={() => { 
                                  setEditId(subscription?.subscriptionPlanId) 
                                  setShowEditModal(true)
                                }}
                                title="Edit Subscription"
                              >
                                <i className="ti ti-edit me-1" /> Edit
                              </Link>
                              <button
                                hidden={!hasAcademicMgntDelete}
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => {
                                  setItemToDelete(subscription);
                                  setShowDeleteModal(true);
                                }}
                                title="Delete Subscription"
                              >
                                <i className="ti ti-trash me-1" /> Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {subscriptions.length === 0 && (
                      <div className="col-12 text-center py-5">
                        <div className="empty-state">
                          <i className="ti ti-search-off fs-1 text-muted mb-3"></i>
                          <h5 className="mb-2">No subscriptions found</h5>
                          <p className="text-muted">No subscriptions match your current filter criteria.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* /Subscription list */}
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
          <CopyRightInfo />
        </div>
      </div>

      <AddSubscription
        defaultCourseId={searchParams.get('courseId') && searchParams.get('courseId') !== 'null' 
          ? parseInt(searchParams.get('courseId') || '0') : undefined}
        show={showAddModal}
        onSuccess={() => {
          setEditId(0);
          fetchSubscriptions();
        }}
        onClose={() => setShowAddModal(false)}
      />

      <ModifySubscription
        show={showEditModal}
        subscriptionId={editId}
        onSuccess={() => {
          setEditId(0);
          fetchSubscriptions();
        }}
        onClose={() => setShowEditModal(false)}
      />
    </>
  );
};

export default Subscriptions;
